// API client that talks to the backend Function App. Auth is acquired one of
// two ways depending on where the SPA is running:
//   - Inside an Office host (PowerPoint task pane): Office SSO via
//     OfficeRuntime.auth.getAccessToken
//   - In a plain browser tab (slides.trackyour.app): MSAL.js popup + silent
//     refresh
//
// App.tsx detects the host and calls setAuthMode before any request fires.
//
// Reference: https://learn.microsoft.com/en-us/office/dev/add-ins/develop/sso-in-office-add-ins
//
// In dev, set VITE_DEV_TOKEN to a manually fetched bearer token to bypass both.

import * as msal from './msal';

const API_BASE = import.meta.env.VITE_API_BASE_URL;
const DEV_TOKEN = import.meta.env.VITE_DEV_TOKEN;

type AuthMode = 'office' | 'web';

let tokenCache: { value: string; expires: number } | null = null;
let officeReady: Promise<void> | null = null;
let authMode: AuthMode = 'office';

export function setAuthMode(mode: AuthMode): void {
  if (authMode === mode) return;
  authMode = mode;
  tokenCache = null;
}

export function initOffice(): Promise<void> {
  if (officeReady) return officeReady;
  officeReady = new Promise<void>((resolve, reject) => {
    if (typeof Office === 'undefined') {
      reject(new Error('Office.js not loaded — are you running outside Office host?'));
      return;
    }
    Office.onReady(() => resolve());
  });
  return officeReady;
}

async function getAuthToken(): Promise<string> {
  if (DEV_TOKEN) return DEV_TOKEN;
  if (tokenCache && tokenCache.expires > Date.now() + 60_000) return tokenCache.value;

  let token: string;
  if (authMode === 'web') {
    token = await msal.getAccessToken();
  } else {
    await initOffice();
    token = await OfficeRuntime.auth.getAccessToken({
      allowSignInPrompt: true,
      allowConsentPrompt: true,
      forMSGraphAccess: false, // backend does its own OBO exchange to call Graph
    });
  }
  tokenCache = { value: token, expires: Date.now() + 55 * 60 * 1000 };
  return token;
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: any) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getAuthToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init.headers || {}),
    },
  });
  if (!res.ok) {
    let body: any;
    try { body = await res.json(); } catch {}
    throw new ApiError(res.status, body?.error || res.statusText, body);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

async function uploadBinary<T>(path: string, file: File | Blob, contentType: string): Promise<T> {
  const token = await getAuthToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': contentType,
      Authorization: `Bearer ${token}`,
    },
    body: file,
  });
  if (!res.ok) {
    let body: any;
    try { body = await res.json(); } catch {}
    throw new ApiError(res.status, body?.error || res.statusText, body);
  }
  return res.json();
}

// --- Types mirror backend's lib/types.ts ---

export type Role = 'viewer' | 'contributor' | 'curator' | 'admin';
export const ROLES: readonly Role[] = ['contributor', 'curator', 'admin'];

export interface MeResponse {
  userId: string;
  userPrincipalName: string;
  tenantId: string;
  /** Resolved role for this request. `admin > curator > contributor`. */
  role: Role;
  /** Convenience: `role === 'admin'`. Kept for back-compat with old checks. */
  isAdmin: boolean;
  setupComplete: boolean;
  canBootstrap: boolean;
}

export type DeckStatus = 'draft' | 'pending_review' | 'published';

export interface DeckVersion {
  v: string; publishedAt: string; publishedBy: string; note?: string;
  pptxBlobUrl: string; current: boolean;
}

export interface Deck {
  id: string; title: string;
  /** Top-level category slug. Must exist in the tenant's CategoryTree. */
  category: string;
  /** Optional sub-category slug. Must exist under `category` in CategoryTree. */
  subCategory?: string;
  /**
   * Display-only team/department label. Free-form, defaults to creator UPN.
   * NOT used for permissions/filtering — `draftedBy` is the author signal.
   */
  owner: string;
  status: DeckStatus; groupIds: string[]; versions: DeckVersion[];
  slideCount: number; draftedBy?: string; submittedAt?: string;
  rejectionReason?: string;
  firstSlideThumbnailUrl?: string;  // SAS-signed at response time (1h), set by worker after split
  createdAt: string; updatedAt: string;
}

export interface CategorySubNode { id: string; name: string; sortOrder: number; }
export interface CategoryNode {
  id: string; name: string; sortOrder: number;
  subCategories: CategorySubNode[];
}
export interface CategoryTree {
  id: 'tree';
  tenantId: string;
  categories: CategoryNode[];
  updatedAt: string;
  updatedBy: string;
}

export interface Curator {
  id: string; userId: string; displayName: string; email: string;
  team?: string;
  role: Role;
  /** @deprecated Use `role`. Will be removed in a future release. */
  isPublisher: boolean;
  invitedAt: string; invitedBy: string;
}

export interface Slide {
  id: string;
  tenantId: string;
  deckId: string;
  version: string;
  position: number;
  title: string;
  thumbnailBlobUrl: string; // SAS-signed at response time (1h)
  pptxBlobUrl: string;      // SAS-signed at response time (1h)
}

export type NotificationKind = 'deck_submitted' | 'deck_published' | 'deck_rejected';
export type NotificationEvent = NotificationKind;

export interface NotificationTemplate {
  id: NotificationEvent;
  tenantId: string;
  event: NotificationEvent;
  emailSubject: string;
  emailBody: string;
  teamsBody: string;
  updatedAt: string;
  updatedBy: string;
}

export interface NotificationLink {
  view: 'manage' | 'browse';
  tab?: 'drafts' | 'review' | 'slides';
  deckId?: string;
}

export interface Notification {
  id: string;
  tenantId: string;
  userId: string;
  kind: NotificationKind;
  title: string;
  body: string;
  link?: NotificationLink;
  read: boolean;
  createdAt: string;
}

export interface NotificationPrefs {
  id: string;
  tenantId: string;
  userId: string;
  email: boolean;
  teams: boolean;
  updatedAt: string;
}

// Branding payload returned by /branding.
export interface Branding {
  appName: string;
  tagline: string;
  accentColor: string;      // '#rrggbb'
  logoBlobUrl: string | null; // SAS-signed at response time (1h) if present
  /** AAD group whose members get the `admin` role. */
  adminsGroupId?: string;
  /** AAD group whose members get the `curator` role. */
  curatorsGroupId?: string;
  /** AAD group whose members get the `contributor` role. */
  contributorsGroupId?: string;
  /** UPN of the service mailbox that notification emails are sent from. */
  notificationSenderUpn?: string;
  /** Tenant-wide kill-switches per channel. Missing or true = on. */
  notificationChannels?: {
    inApp?: boolean;
    email?: boolean;
    teams?: boolean;
  };
  teamsFallbackChannel?: {
    teamId: string;
    teamName: string;
    channelId: string;
    channelName: string;
  };
  updatedAt: string;
  updatedBy: string;
}

export interface TeamRef { id: string; displayName: string; description?: string }
export interface ChannelRef { id: string; displayName: string; description?: string }

// AAD group reference returned by /groups/search and /groups/lookup.
export interface GraphGroup {
  id: string;
  displayName: string;
  description?: string;
}

export const api = {
  me: () => request<MeResponse>('/me'),
  bootstrap: (adminGroupId: string) =>
    request<{ ok: true }>('/bootstrap', { method: 'POST', body: JSON.stringify({ adminGroupId }) }),

  listDecks: () => request<Deck[]>('/decks'),
  getDeck: (id: string) => request<Deck>(`/decks/${id}`),
  listDeckSlides: (deckId: string) => request<Slide[]>(`/decks/${deckId}/slides`),
  createDeck: (body: { title: string; category: string; subCategory?: string; owner: string; groupIds?: string[] }) =>
    request<Deck>('/decks', { method: 'POST', body: JSON.stringify(body) }),

  listCategories: () => request<CategoryTree>('/categories'),
  updateCategories: (categories: CategoryNode[]) =>
    request<CategoryTree>('/categories', { method: 'PUT', body: JSON.stringify({ categories }) }),

  // Re-enqueues the slide-split worker for a deck's current version. Use it
  // to recover decks where all slides were deleted (or the original split
  // failed). Worker writes are idempotent on deterministic IDs.
  reprocessDeck: (id: string) =>
    request<{ ok: true; version: string }>(`/decks/${id}/reprocess`, { method: 'POST' }),

  uploadDeck: (id: string, file: File, note?: string) => {
    const q = note ? `?note=${encodeURIComponent(note)}` : '';
    const isPotx = /\.potx$/i.test(file.name);
    const contentType = isPotx
      ? 'application/vnd.openxmlformats-officedocument.presentationml.template'
      : 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    return uploadBinary<Deck>(`/decks/${id}/upload${q}`, file, contentType);
  },

  renameDeck: (id: string, title: string) =>
    request<Deck>(`/decks/${id}`, { method: 'PATCH', body: JSON.stringify({ title }) }),
  patchDeck: (id: string, patch: { title?: string; category?: string; subCategory?: string | null; owner?: string; groupIds?: string[] }) =>
    request<Deck>(`/decks/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  updateDeckGroups: (id: string, groupIds: string[]) =>
    request<Deck>(`/decks/${id}`, { method: 'PATCH', body: JSON.stringify({ groupIds }) }),

  searchGroups: (q: string) =>
    request<{ groups: GraphGroup[] }>(`/groups/search?q=${encodeURIComponent(q)}`),
  lookupGroups: (ids: string[]) =>
    request<{ groups: GraphGroup[] }>(`/groups/lookup`, { method: 'POST', body: JSON.stringify({ ids }) }),

  deleteDeck: (id: string) => request<void>(`/decks/${id}`, { method: 'DELETE' }),
  deleteSlide: (deckId: string, slideId: string) =>
    request<void>(`/decks/${deckId}/slides/${slideId}`, { method: 'DELETE' }),
  renameSlide: (deckId: string, slideId: string, title: string) =>
    request<Slide>(`/decks/${deckId}/slides/${slideId}`, { method: 'PATCH', body: JSON.stringify({ title }) }),
  requestSlideInsert: (deckId: string, slideId: string) =>
    request<{ downloadUrl: string; expiresAt: string; insertEventId: string }>(
      `/decks/${deckId}/slides/${slideId}/insert`,
      { method: 'POST' }
    ),

  submitDeck: (id: string) => request<Deck>(`/decks/${id}/submit`, { method: 'POST' }),
  publishDeck: (id: string) => request<Deck>(`/decks/${id}/publish`, { method: 'POST' }),
  rejectDeck: (id: string, reason: string) =>
    request<Deck>(`/decks/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),

  listCurators: () => request<{ curators: Curator[]; pendingInvites: any[] }>('/curators'),
  inviteCurator: (email: string, role: Role) =>
    request('/curators/invite', { method: 'POST', body: JSON.stringify({ email, role }) }),
  updateCuratorRole: (userId: string, role: Role) =>
    request<Curator>(`/curators/${encodeURIComponent(userId)}`, { method: 'PATCH', body: JSON.stringify({ role }) }),
  removeCurator: (userId: string) =>
    request<void>(`/curators/${encodeURIComponent(userId)}`, { method: 'DELETE' }),

  getBranding: () => request<Branding>('/branding'),
  updateBranding: (body: {
    appName?: string;
    tagline?: string;
    accentColor?: string;
    adminsGroupId?: string | null;
    curatorsGroupId?: string | null;
    contributorsGroupId?: string | null;
    notificationSenderUpn?: string | null;
    notificationChannels?: { inApp?: boolean | null; email?: boolean | null; teams?: boolean | null } | null;
    teamsFallbackChannel?: Branding['teamsFallbackChannel'] | null;
  }) => request<Branding>('/branding', { method: 'PATCH', body: JSON.stringify(body) }),
  uploadBrandingLogo: (file: File) => uploadBinary<Branding>('/branding/logo', file, file.type || 'application/octet-stream'),
  deleteBrandingLogo: () => request<Branding>('/branding/logo', { method: 'DELETE' }),

  listJoinedTeams: () => request<{ teams: TeamRef[] }>('/teams/mine'),
  listTeamChannels: (teamId: string) => request<{ channels: ChannelRef[] }>(`/teams/${encodeURIComponent(teamId)}/channels`),

  listNotifications: (limit = 50) =>
    request<{ notifications: Notification[]; unreadCount: number }>(`/notifications?limit=${limit}`),
  markNotificationRead: (id: string) =>
    request<void>(`/notifications/${id}/read`, { method: 'POST' }),
  markAllNotificationsRead: () =>
    request<{ marked: number }>(`/notifications/read-all`, { method: 'POST' }),

  getNotificationPrefs: () => request<NotificationPrefs>('/me/notification-prefs'),
  updateNotificationPrefs: (body: { email?: boolean; teams?: boolean }) =>
    request<NotificationPrefs>('/me/notification-prefs', { method: 'PATCH', body: JSON.stringify(body) }),

  listNotificationTemplates: () =>
    request<{ templates: NotificationTemplate[] }>('/notification-templates'),
  updateNotificationTemplate: (event: NotificationEvent, body: { emailSubject: string; emailBody: string; teamsBody: string }) =>
    request<NotificationTemplate>(`/notification-templates/${event}`, { method: 'PUT', body: JSON.stringify(body) }),
  resetNotificationTemplate: (event: NotificationEvent) =>
    request<NotificationTemplate>(`/notification-templates/${event}/reset`, { method: 'POST' }),
  testNotificationTemplate: (event: NotificationEvent, body: { to: string; channel: 'email' | 'teams' }) =>
    request<{ sent: boolean; delivered?: string; error?: string }>(`/notification-templates/${event}/test`, { method: 'POST', body: JSON.stringify(body) }),

  trackInsertion: (deckId: string, slideId?: string) =>
    request('/events/insertion', { method: 'POST', body: JSON.stringify({ deckId, slideId }) }),

  analytics: (since?: string) => {
    const q = since ? `?since=${encodeURIComponent(since)}` : '';
    return request<{ totals: any; byDeck: any[]; since: string }>(`/analytics${q}`);
  },
};

// Office.js global type stubs — real types come from @types/office-js if installed
declare const Office: any;
declare const OfficeRuntime: any;
